import { matchPath } from 'react-router-dom';

/**
 * Loaders for each lazily loaded page. {@link AppRoutes} renders pages through these, and
 * {@link preloadPageForPath} calls them early; repeated calls share the same load.
 */
export const pageLoaders = {
    dashboard: () => import('pages/dashboard/Dashboard'),
    datasourceOverview: () => import('pages/datasources/Datasource'),
    datasourceDashboard: () => import('pages/datasources/DatasourceDashboard'),
    datasources: () => import('pages/datasources/DatasourcesPage'),
    pluginLoadingModalTest: () => import('pages/dev/PluginLoadingModalTestPage'),
    drilldown: () => import('pages/drilldowns/Drilldown'),
    explore: () => import('pages/explore/Explore'),
    explorer: () => import('pages/explorer/Explorer'),
    monitoring: () => import('pages/monitoring/Monitoring'),
    scope: () => import('pages/scope/Scope'),
    scopes: () => import('pages/scope/ScopesPage'),
    settings: () => import('pages/settings/Settings'),
    status: () => import('pages/status/StatusPage'),
    usage: () => import('pages/usage/UsagePage'),
    workspaceHome: () => import('pages/workspaceHome/WorkspaceHome')
};

/**
 * Must match the route paths in {@link AppRoutes}. More specific paths come first.
 */
const pageLoadersByPath: [string, () => Promise<unknown>][] = [
    ['/status', pageLoaders.status],
    ['/workspace/:id/overview', pageLoaders.workspaceHome],
    ['/drilldown/*', pageLoaders.drilldown],
    ['/dashboard/:id', pageLoaders.dashboard],
    ['/monitoring/:id', pageLoaders.monitoring],
    ['/explorer/:id', pageLoaders.explorer],
    ['/explore', pageLoaders.explore],
    ['/datasources/:id', pageLoaders.datasources],
    ['/scope/:id', pageLoaders.scope],
    ['/scopes/:id', pageLoaders.scopes],
    ['/datasource/:id/dashboard/*', pageLoaders.datasourceDashboard],
    ['/datasource/:id', pageLoaders.datasourceOverview],
    ['/settings/*', pageLoaders.settings],
    ['/usage', pageLoaders.usage],
    ['/dev/loading-modal', pageLoaders.pluginLoadingModalTest]
];

/**
 * Starts loading the page for `pathname`, if it is a lazily loaded page, so its code downloads in
 * parallel with the app shell rather than after it. Load failures are left to surface when the page renders.
 */
export const preloadPageForPath = (pathname: string): void => {
    const loader = pageLoadersByPath.find(([path]) => matchPath(path, pathname))?.[1];

    loader?.().catch(() => undefined);
};
